import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { format } from 'date-fns';
import { id as localeId } from 'date-fns/locale';
import { Transaction } from '../types';
import transactionService from '../services/transactionService';

interface Props {
  open: boolean;
  transaction: Transaction | null;
  onClose: () => void;
  onUpdated: () => void;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd MMMM yyyy, HH:mm', { locale: localeId });
  } catch {
    return '-';
  }
};

const TYPE_LABELS: Record<string, string> = {
  topup: 'Top Up',
  payment: 'Pembayaran',
  deduction: 'Pemotongan',
  refund: 'Refund',
};

const STATUS_LABELS: Record<string, string> = {
  completed: 'Selesai',
  pending: 'Pending',
  failed: 'Gagal',
  cancelled: 'Dibatalkan',
};

const TransactionDetailDialog: React.FC<Props> = ({ open, transaction, onClose, onUpdated }) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStatusUpdate = async (status: 'completed' | 'failed' | 'cancelled') => {
    if (!transaction) return;
    setLoading(true);
    setError(null);
    try {
      await transactionService.updateTransactionStatus(transaction.id, { status, notes: notes || undefined });
      onUpdated();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gagal memperbarui status transaksi');
    } finally {
      setLoading(false);
    }
  };

  if (!transaction) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Detail Transaksi</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Kode
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {transaction.transaction_code}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Jumlah
            </Typography>
            <Typography variant="body1" fontWeight={700} color="primary.main">
              {formatCurrency(transaction.amount)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Jenis
            </Typography>
            <Box mt={0.5}>
              <Chip label={TYPE_LABELS[transaction.type] || transaction.type} size="small" variant="outlined" />
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box mt={0.5}>
              <Chip label={STATUS_LABELS[transaction.status] || transaction.status} size="small" color="primary" />
            </Box>
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography variant="caption" color="text.secondary">
              Pelanggan
            </Typography>
            <Typography variant="body1">
              {transaction.customer?.name || '-'}
              {transaction.customer?.customer_code ? ` (${transaction.customer.customer_code})` : ''}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Metode Pembayaran
            </Typography>
            <Typography variant="body1">{transaction.payment_method || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Referensi
            </Typography>
            <Typography variant="body1">{transaction.reference_id || transaction.external_reference || '-'}</Typography>
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography variant="caption" color="text.secondary">
              Deskripsi
            </Typography>
            <Typography variant="body1">{transaction.description || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Dibuat
            </Typography>
            <Typography variant="body2">{formatDate(transaction.createdAt)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Diproses
            </Typography>
            <Typography variant="body2">
              {formatDate(transaction.processed_at)}
              {transaction.processor?.name ? ` oleh ${transaction.processor.name}` : ''}
            </Typography>
          </Box>
        </Box>

        {transaction.status === 'pending' && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Perbarui Status
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Catatan (opsional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Tutup
        </Button>
        {transaction.status === 'pending' && (
          <>
            <Button
              color="error"
              variant="outlined"
              disabled={loading}
              onClick={() => handleStatusUpdate('cancelled')}
            >
              Batalkan
            </Button>
            <Button
              color="warning"
              variant="outlined"
              disabled={loading}
              onClick={() => handleStatusUpdate('failed')}
            >
              Gagal
            </Button>
            <Button
              variant="contained"
              disabled={loading}
              onClick={() => handleStatusUpdate('completed')}
              startIcon={loading ? <CircularProgress size={16} /> : undefined}
            >
              Tandai Selesai
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TransactionDetailDialog;
